import React from 'react'
import {
  BooleanInput,
  FormDataConsumer,
  Labeled,
  SelectInput,
  useTranslate,
} from 'react-admin'
import { APIEntity } from 'services/api/entities'
import { productStatusChoices } from 'services/cfg/form'
import { NamedService } from 'types/common'
import CustomizationInput from 'ui-shared/FormComponents/CustomizationInput'
import EventServiceBriefSection from 'ui-shared/FormComponents/EventServiceBriefSection'
import Kdivider from 'ui-shared/LayoutComponents/Kdivider'
import KRichTextInput from 'ui-shared/WrappedInputs/KRichTextInput'
import { isOptionedObsolete } from '../../utils'
import { EventFormDataConsumerProps } from '../GeneralTab'
import { PRODUCT_SERVICE_ENTITY_MAP } from 'services/utils/name-maps'
import { TailoredEventSelectionInput } from 'ui-shared/FormComponents/TailoredEventSelectionInput'
import ExperienceSelectionDetailsForm from './ExperienceSelectionDetailsForm'
import KFormTab from 'ui-shared/WrappedInputs/KFormTab'
import { Badge } from '@mui/material'
import useSelections from '../hooks/useSelections'
import useService from '../hooks/useService'

type Props = {
  experience: Extract<APIEntity, 'virtuals' | 'physicals'>
  experienceService: Extract<NamedService, 'physicalService' | 'virtualService'>
  children?: React.ReactNode
}

const ExperienceFormTab: React.FC<Props> = ({
  experience,
  children,
  experienceService,
  ...rest
}) => {
  const linkedProductType = PRODUCT_SERVICE_ENTITY_MAP[experience]

  const { hasSelections } = useSelections(experience)
  const { hasCustomization } = useService(experienceService)

  const translate = useTranslate()

  return (
    <KFormTab
      {...rest}
      label={
        <Badge
          badgeContent={hasSelections || hasCustomization ? 1 : 0}
          color="primary"
          variant="dot"
        >
          {translate(`form.tabs.${experience}`)}
        </Badge>
      }
    >
      <SelectInput
        source={`${linkedProductType}ExperiencesStatus`}
        label="form.fields.status"
        defaultValue={productStatusChoices[0].id}
        choices={productStatusChoices}
      />

      <KRichTextInput
        source={`${linkedProductType}ExperiencesNotes`}
        label="form.fields.notes"
      />

      {children}

      {/* Experience input */}
      <TailoredEventSelectionInput
        entity={experience}
        label="form.fields.orders"
        source={experience}
        getIsProductObsolete={isOptionedObsolete}
        disableProductChoiceAfterSelections
      >
        <ExperienceSelectionDetailsForm experience={experience} />
      </TailoredEventSelectionInput>

      <FormDataConsumer>
        {({ formData }: EventFormDataConsumerProps) => {
          const service = formData.productServices?.[experienceService]

          return (
            <React.Fragment>
              <CustomizationInput
                source={`productServices.${experienceService}.customization`}
                customization={service?.customization}
              />
              {service?.briefQuestions && service?.briefQuestions.length ? (
                <React.Fragment>
                  <Kdivider title="Event Brief" headingLevel="h3" />
                  <Labeled label="form.fields.briefConfirmation">
                    <BooleanInput
                      source={`productServices.${experienceService}.briefConfirmed`}
                      label={service?.briefConfirmed ? 'Yes' : 'No'}
                      disabled
                    />
                  </Labeled>
                  <EventServiceBriefSection
                    serviceSource={`productServices.${experienceService}`}
                  />
                </React.Fragment>
              ) : null}
            </React.Fragment>
          )
        }}
      </FormDataConsumer>
    </KFormTab>
  )
}

export default ExperienceFormTab
